import React, { useEffect, useMemo, useState } from "react";
import {
  FiPlus,
  FiSearch,
  FiRefreshCw,
  FiEdit2,
  FiArchive,
  FiRotateCcw,
  FiBookOpen,
  FiAlertCircle,
  FiX,
  FiChevronDown,
  FiChevronRight,
  FiLayers,
} from "react-icons/fi";
import {
  coaService,
  ACCOUNT_NORMAL_SIDE,
  normalizeAccountType,
} from "../services/coaService";
import COAModal from "../components/accounting/COAModal";
import DeleteAccountModal from "../components/accounting/DeleteAccountModal";
import { useClient } from "../context/ClientContext";

const TYPES = [
  { id: "all", label: "All accounts" },
  { id: "Asset", label: "Assets" },
  { id: "Liability", label: "Liabilities" },
  { id: "Equity", label: "Equity" },
  { id: "Income", label: "Income" },
  { id: "Expense", label: "Expenses" },
];
const order = { Asset: 0, Liability: 1, Equity: 2, Income: 3, Expense: 4 };
const text = (value) => String(value || "").toLowerCase();

export default function ChartOfAccounts() {
  const { activeClient } = useClient();
  const organizationId = activeClient?.id;
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [collapsed, setCollapsed] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [defaultType, setDefaultType] = useState("Asset");
  const [defaultSubType, setDefaultSubType] = useState(null);
  const [accountToDelete, setAccountToDelete] = useState(null);

  const loadAccounts = async (org = organizationId) => {
    if (!org) return;
    setLoading(true);
    try {
      const rows = await coaService.getAccounts(org);
      if (org === organizationId) setAccounts(rows);
    } catch (err) {
      if (org === organizationId)
        setError(err.message || "Unable to load accounts.");
    } finally {
      if (org === organizationId) setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setAccounts([]);
    setError("");
    setSuccess("");
    setIsModalOpen(false);
    setAccountToDelete(null);
    if (!organizationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    coaService
      .getAccounts(organizationId)
      .then((rows) => {
        if (!cancelled) setAccounts(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Unable to load accounts.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const counts = useMemo(
    () =>
      accounts.reduce((result, account) => {
        const type = normalizeAccountType(account.type);
        result[type] = (result[type] || 0) + 1;
        return result;
      }, {}),
    [accounts],
  );

  const filtered = useMemo(
    () =>
      accounts
        .filter((account) => {
          const type = normalizeAccountType(account.type);
          if (typeFilter !== "all" && type !== typeFilter) return false;
          if (statusFilter === "active" && !account.is_active) return false;
          if (statusFilter === "archived" && account.is_active) return false;
          const q = text(search.trim());
          return (
            !q ||
            [
              account.code,
              account.name,
              account.sub_type,
              account.description,
              type,
            ].some((value) => text(value).includes(q))
          );
        })
        .sort((a, b) => {
          const typeDiff =
            (order[normalizeAccountType(a.type)] ?? 99) -
            (order[normalizeAccountType(b.type)] ?? 99);
          return (
            typeDiff ||
            text(a.sub_type).localeCompare(text(b.sub_type)) ||
            text(a.code).localeCompare(text(b.code), undefined, {
              numeric: true,
            }) ||
            text(a.name).localeCompare(text(b.name))
          );
        }),
    [accounts, typeFilter, statusFilter, search],
  );

  const groups = useMemo(() => {
    const result = new Map();
    filtered.forEach((account) => {
      const type = normalizeAccountType(account.type);
      const sub = account.sub_type || "Other";
      const key = `${type}::${sub}`;
      if (!result.has(key)) result.set(key, { key, type, sub, accounts: [] });
      result.get(key).accounts.push(account);
    });
    return [...result.values()];
  }, [filtered]);

  const addAccount = (type = null, subType = null) => {
    setEditingAccount(null);
    setDefaultType(type || (typeFilter !== "all" ? typeFilter : "Asset"));
    setDefaultSubType(subType);
    setIsModalOpen(true);
  };

  const editAccount = (account) => {
    setEditingAccount(account);
    setDefaultType(normalizeAccountType(account.type));
    setDefaultSubType(null);
    setIsModalOpen(true);
  };

  const archiveAccount = async () => {
    if (!accountToDelete || !organizationId || busy) return;
    setBusy(true);
    setError("");
    try {
      await coaService.deleteAccount(accountToDelete.id, organizationId);
      setAccountToDelete(null);
      await loadAccounts();
      setSuccess(
        "Account archived. Its historical ledger records remain available.",
      );
    } catch (err) {
      setError(err.message || "Unable to archive account.");
    } finally {
      setBusy(false);
    }
  };

  const restoreAccount = async (account) => {
    if (!organizationId || busy) return;
    setBusy(true);
    setError("");
    try {
      await coaService.restoreAccount(account.id, organizationId);
      await loadAccounts();
      setSuccess("Account restored successfully.");
    } catch (err) {
      setError(err.message || "Unable to restore account.");
    } finally {
      setBusy(false);
    }
  };

  const total = accounts.length;
  const active = accounts.filter((a) => a.is_active).length;
  const archived = total - active;

  if (!organizationId)
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <FiBookOpen className="mb-4 text-4xl text-slate-300" />
        <h2 className="text-lg font-semibold text-slate-900">
          Select a business
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Choose a business to view its chart of accounts.
        </p>
      </div>
    );

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-24">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Accounting / General ledger
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Chart of Accounts
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            A clear, organized directory of your business accounts.
          </p>
        </div>
        <button
          onClick={() => addAccount()}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700"
        >
          <FiPlus /> New account
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <FiAlertCircle className="shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError("")} aria-label="Dismiss error">
            <FiX />
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess("")} aria-label="Dismiss success">
            <FiX />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          ["Total accounts", total],
          ["Active accounts", active],
          ["Archived accounts", archived],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded-2xl border border-slate-200 bg-white p-5"
          >
            <p className="text-sm text-slate-500">{label}</p>
            <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
              {value}
            </p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-5 pt-5">
          <div className="flex gap-1 overflow-x-auto pb-4">
            {TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => setTypeFilter(type.id)}
                className={`whitespace-nowrap rounded-xl px-3.5 py-2 text-sm font-semibold transition-colors ${typeFilter === type.id ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"}`}
              >
                {type.label}
                <span
                  className={`ml-2 text-xs ${typeFilter === type.id ? "text-slate-300" : "text-slate-400"}`}
                >
                  {type.id === "all" ? total : counts[type.id] || 0}
                </span>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 pb-5">
            <div className="relative min-w-[220px] flex-1">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, code or category..."
                className="w-full rounded-xl border border-slate-200 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
            <button
              onClick={() => loadAccounts()}
              disabled={loading}
              className="rounded-xl border border-slate-200 p-3 text-slate-500 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Refresh accounts"
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex min-h-64 items-center justify-center gap-3 text-sm text-slate-500">
            <FiRefreshCw className="animate-spin" /> Loading accounts...
          </div>
        ) : groups.length === 0 ? (
          <div className="flex min-h-64 flex-col items-center justify-center px-5 text-center">
            <FiLayers className="mb-3 text-3xl text-slate-300" />
            <h3 className="font-semibold text-slate-800">No accounts found</h3>
            <p className="mt-1 text-sm text-slate-500">
              Try another filter or create a new account.
            </p>
          </div>
        ) : (
          <div>
            {groups.map((group) => {
              const isCollapsed = Boolean(collapsed[group.key]);
              return (
                <div
                  key={group.key}
                  className="border-b border-slate-100 last:border-0"
                >
                  <div className="flex items-center justify-between bg-slate-50/70 px-5 py-3">
                    <button
                      className="flex min-w-0 items-center gap-2 text-left"
                      onClick={() =>
                        setCollapsed((prev) => ({
                          ...prev,
                          [group.key]: !prev[group.key],
                        }))
                      }
                    >
                      {isCollapsed ? (
                        <FiChevronRight className="shrink-0 text-slate-400" />
                      ) : (
                        <FiChevronDown className="shrink-0 text-slate-400" />
                      )}
                      <span className="text-sm font-semibold text-slate-800">
                        {group.sub}
                      </span>
                      <span className="rounded-md bg-white px-2 py-0.5 text-xs text-slate-500">
                        {group.accounts.length}
                      </span>
                      <span className="hidden text-xs text-slate-400 sm:inline">
                        {group.type}
                      </span>
                    </button>
                    <button
                      onClick={() => addAccount(group.type, group.sub)}
                      className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                    >
                      <FiPlus /> Add
                    </button>
                  </div>
                  {!isCollapsed && (
                    <div className="divide-y divide-slate-100">
                      {group.accounts.map((account) => (
                        <div
                          key={account.id}
                          className={`group flex flex-wrap items-center gap-3 px-5 py-4 hover:bg-slate-50/60 ${!account.is_active ? "bg-slate-50/40" : ""}`}
                        >
                          <div className="w-16 shrink-0 text-xs font-semibold tabular-nums text-slate-400">
                            {account.code || "—"}
                          </div>
                          <div className="min-w-[180px] flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-semibold text-slate-900">
                                {account.name}
                              </span>
                              {!account.is_active && (
                                <span className="rounded-md bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                                  Archived
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-xs text-slate-500">
                              {account.description || account.sub_type}
                            </p>
                          </div>
                          <div className="w-24 text-right text-xs text-slate-500">
                            {account.currency}
                          </div>
                          <div className="w-20 text-right text-xs text-slate-500">
                            {ACCOUNT_NORMAL_SIDE[
                              normalizeAccountType(account.type)
                            ] || "—"}
                          </div>
                          <div className="flex w-20 justify-end gap-1">
                            {account.is_active ? (
                              <>
                                <button
                                  title="Edit account"
                                  aria-label={`Edit ${account.name}`}
                                  onClick={() => editAccount(account)}
                                  className="rounded-lg p-2 text-slate-500 hover:bg-blue-50 hover:text-blue-600"
                                >
                                  <FiEdit2 />
                                </button>
                                <button
                                  title="Archive account"
                                  aria-label={`Archive ${account.name}`}
                                  onClick={() => setAccountToDelete(account)}
                                  className="rounded-lg p-2 text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                                >
                                  <FiArchive />
                                </button>
                              </>
                            ) : (
                              <button
                                title="Restore account"
                                aria-label={`Restore ${account.name}`}
                                disabled={busy}
                                onClick={() => restoreAccount(account)}
                                className="rounded-lg p-2 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                              >
                                <FiRotateCcw />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="border-t border-slate-100 px-5 py-4 text-xs text-slate-500">
          Showing {filtered.length} of {total} accounts · Debit/Credit indicates
          normal balance, not current balance.
        </div>
      </div>

      <COAModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => loadAccounts()}
        initialData={editingAccount}
        defaultType={defaultType}
        defaultSubType={defaultSubType}
      />
      <DeleteAccountModal
        account={accountToDelete}
        isDeleting={busy}
        onCancel={() => setAccountToDelete(null)}
        onConfirm={archiveAccount}
      />
    </div>
  );
}